import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Paper, TextField, IconButton, CircularProgress, Stack, Chip, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AIThreadList from './AIThreadList';
import SendIcon from '@mui/icons-material/Send';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PersonIcon from '@mui/icons-material/Person';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, orderBy, query, doc, setDoc, addDoc, serverTimestamp } from 'firebase/firestore';

import { db } from '../firebase';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt?: any;
}

interface AIAssistantChatProps {
  tenantId: string;
  userId: string;
  threadId?: string;
  onThreadCreated?: (threadId: string) => void;
  suggestedPrompts?: string[];
  title?: string;
  onOpenRequested?: () => void;
  showThreadListPanel?: boolean; // when false, hide internal history panel (dash provides its own)
  // Optional: auto-seed chat with a prompt (used by dashboard chips)
  initialPrompt?: string;
  onInitialPromptConsumed?: () => void;
  // When true and no threadId is supplied, do not create a Firestore thread
  // until the user clicks New. Messages are kept locally (ephemeral).
  ephemeralUntilSaved?: boolean;
}

const AIAssistantChat: React.FC<AIAssistantChatProps> = ({ tenantId, userId, threadId, onThreadCreated, suggestedPrompts, title = 'AI Assistant', onOpenRequested, showThreadListPanel = true, initialPrompt, onInitialPromptConsumed, ephemeralUntilSaved = undefined }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [seed, setSeed] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const functions = useMemo(() => getFunctions(), []);

  const isEphemeral = (ephemeralUntilSaved ?? (title === 'Sales Coach')) === true;

  const ensureThread = useCallback(async () => {
    if (threadId) return threadId;
    if (isEphemeral) return undefined as unknown as string;
    const startAIThread = httpsCallable(functions, 'startAIThread');
    const res = await startAIThread({ tenantId, context: 'assistant' });
    const id = (res.data as any)?.threadId as string;
    onThreadCreated?.(id);
    return id;
  }, [functions, onThreadCreated, tenantId, threadId, isEphemeral]);

  // Live subscription to messages when thread is ready
  useEffect(() => {
    if (!tenantId || !threadId) return;
    const q = query(
      collection(db, 'tenants', tenantId, 'ai_chats', threadId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const list: ChatMessage[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      setMessages(list);
    });
    return () => unsub();
  }, [tenantId, threadId]);

  // Allow parent to change threadId and restore messages
  useEffect(() => {
    // The subscription above reacts to threadId change and restores history automatically
  }, [threadId]);

  const sendMessage = useCallback(async () => {
    if (!input.trim()) return;
    const userMsg: ChatMessage = {
      id: `${Date.now()}`,
      role: 'user',
      content: input.trim()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const currentThreadId = await ensureThread();

      // Update conversation title only when we have a real thread
      if (currentThreadId) {
        try {
          await setDoc(doc(db, 'tenants', tenantId, 'ai_chats', currentThreadId), { title: userMsg.content.slice(0, 80) }, { merge: true });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Failed to set chat title:', e);
        }
      }

      // Use direct fetch - function uses onRequest (HTTP) not onCall (callable)
      // CORS is handled by the function, but needs to allow localhost for dev
      const origin = window.location.origin;
      const response = await fetch(`https://us-central1-hrx1-d3beb.cloudfunctions.net/enhancedChatWithGPT`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, userId, threadId: currentThreadId || 'ephemeral', messages: [...messages, userMsg].slice(-10) })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const assistantMsg: ChatMessage = {
        id: `${Date.now()}_assistant`,
        role: 'assistant',
        content: data?.reply || 'No response'
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      const assistantMsg: ChatMessage = {
        id: `${Date.now()}_assistant_error`,
        role: 'assistant',
        content: 'Sorry, I could not process that right now.'
      };
      setMessages(prev => [...prev, assistantMsg]);
      // eslint-disable-next-line no-console
      console.error('AI chat error:', err);
    } finally {
      setLoading(false);
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }), 50);
    }
  }, [ensureThread, input, messages, tenantId, userId]);

  const handleSuggestionClick = useCallback(async (prompt: string) => {
    try {
      onOpenRequested?.();
      const userMsg: ChatMessage = { id: `${Date.now()}`, role: 'user', content: prompt };
      setMessages([userMsg]);
      setLoading(true);
      let newId: string | undefined;
      if (!isEphemeral) {
        const startAIThread = httpsCallable(functions, 'startAIThread');
        const res = await startAIThread({ tenantId, context: 'assistant' });
        newId = (res.data as any)?.threadId as string;
        onThreadCreated?.(newId);
        try {
          await setDoc(doc(db, 'tenants', tenantId, 'ai_chats', newId), { title: prompt.slice(0, 80) }, { merge: true });
        } catch (e) {
          console.warn('Failed to set chat title from suggestion:', e);
        }
      }
      // Use direct fetch - function uses onRequest (HTTP) not onCall (callable)
      // CORS is handled by the function, but needs to allow localhost for dev
      const response = await fetch(`https://us-central1-hrx1-d3beb.cloudfunctions.net/enhancedChatWithGPT`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, userId, threadId: newId || 'ephemeral', messages: [userMsg] })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const assistantMsg: ChatMessage = { id: `${Date.now()}_assistant`, role: 'assistant', content: data?.reply || 'No response' };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      const assistantMsg: ChatMessage = { id: `${Date.now()}_assistant_error`, role: 'assistant', content: 'Could not start chat.' };
      setMessages(prev => [...prev, assistantMsg]);
      // eslint-disable-next-line no-console
      console.error('Suggestion start error:', err);
    } finally {
      setLoading(false);
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }), 50);
    }
  }, [functions, onOpenRequested, onThreadCreated, tenantId, userId]);

  useEffect(() => {
    setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }), 50);
  }, [messages.length]);

  // Auto-start with initialPrompt (ephemeral-friendly)
  const [seeded, setSeeded] = useState<string>('');
  useEffect(() => {
    const p = (initialPrompt || '').trim();
    if (!p || p === seeded) return;
    (async () => {
      try {
        await handleSuggestionClick(p);
        setSeeded(p);
        onInitialPromptConsumed?.();
      } catch (e) {
        console.error('initialPrompt error', e);
      }
    })();
  }, [initialPrompt, seeded, handleSuggestionClick, onInitialPromptConsumed]);

  // Persist current ephemeral conversation and start a fresh one
  const persistAndReset = useCallback(async () => {
    if (!isEphemeral || threadId || messages.length === 0) {
      setMessages([]);
      setInput('');
      return;
    }
    try {
      const startAIThread = httpsCallable(functions, 'startAIThread');
      const res = await startAIThread({ tenantId, context: 'assistant' });
      const newId = (res.data as any)?.threadId as string;
      onThreadCreated?.(newId);
      const firstUser = messages.find(m => m.role === 'user');
      if (firstUser?.content) {
        await setDoc(doc(db, 'tenants', tenantId, 'ai_chats', newId), { title: firstUser.content.slice(0, 80) }, { merge: true });
      }
      for (const m of messages) {
        await addDoc(collection(db, 'tenants', tenantId, 'ai_chats', newId, 'messages'), {
          role: m.role,
          content: m.content,
          createdAt: serverTimestamp(),
          userId,
        });
      }
    } catch (e) {
      console.error('Failed to persist conversation', e);
    } finally {
      setMessages([]);
      setInput('');
    }
  }, [isEphemeral, threadId, messages, functions, onThreadCreated, tenantId, userId]);

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'row', height: '100%', borderRadius: 1, gap: showThreadListPanel ? 3 : 0 }}>
      {/* Left: historical threads (optional) */}
      {showThreadListPanel && (
        <Box sx={{ width: 300, borderRight: '1px solid', borderColor: 'divider', height: '100%', overflow: 'hidden' }}>
          <AIThreadList
            tenantId={tenantId}
            userId={userId}
            selectedThreadId={threadId}
            onSelect={(id) => onThreadCreated?.(id)}
            onCreate={() => {}}
          />
        </Box>
      )}
      {/* Right: live chat column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Messages area */}
        <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              {(suggestedPrompts && suggestedPrompts.length > 0
                ? suggestedPrompts
                : ['Ask me anything', 'Draft a professional email', 'Summarize these notes', 'Brainstorm outreach ideas']
               ).map((t) => (
                <Chip key={t + seed} label={t} variant="outlined" onClick={() => handleSuggestionClick(t)} />
              ))}
            </Box>
          </Box>
        )}
        <Stack spacing={2}>
          {messages.map((m) => (
            <Box key={m.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ mt: '2px', color: m.role === 'user' ? 'primary.main' : 'secondary.main' }}>
                {m.role === 'user' ? <PersonIcon fontSize="small" /> : <PsychologyIcon fontSize="small" />}
              </Box>
              <Paper variant="outlined" sx={{ p: 1.5, maxWidth: '900px', bgcolor: m.role === 'user' ? 'grey.50' : 'white' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
              </Paper>
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Thinking…</Typography>
            </Box>
          )}
        </Stack>
        </Box>
        {/* Input area */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Message AI…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          />
          <IconButton color="primary" onClick={sendMessage} disabled={loading || !input.trim()}>
            {loading ? <CircularProgress size={18} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default AIAssistantChat;


